import clsx from 'clsx';
import React, { FC, useMemo, useEffect } from 'react';
import { useTable, useExpanded, useRowState } from 'react-table';
import { TO_MANY_DESCENDANTS } from '../../../hooks/useMarketingPreferences';
import { Tooltip } from '@td-client-frontend/common';
import { InfoInCircle } from '@td-client-frontend/icons';
import { MarketingHierarchyData, MarketingHierarchyRowData } from '../../../@types/Marketing';
import { Nullable } from '../../../@types/common';

interface MarketingTable {
  columns: any;
  data: MarketingHierarchyData[];
  selectedEntity: Nullable<MarketingHierarchyRowData>;
}

const getCollapsedOrgsIds = (initial: MarketingHierarchyData[], result: string[]) => {
  initial?.forEach((el) => {
    if (el?.hiddenSubRows) {
      result.push(el.id);
    }
    if (el?.subRows?.length) {
      getCollapsedOrgsIds(el.subRows, result);
    }
  });
};

const ToManyDescendants = () => (
  <td style={{ height: '47px' }} className="flex items-center py-2" data-testid="toManyDescendantsItem">
    <Tooltip className="w-60" id="toManyDescendantsTooltip" place="top" visible type="dark" border={false} />
    <InfoInCircle
      className="w-4 h-4"
      data-for="toManyDescendantsTooltip"
      data-tip="We can only display the first 50 groups at this time. If you are looking for specific group details, you will need to perform a direct search for that group."
    />
    <span className="ml-1 text-gray-700 text-lg leading-7">First 50 groups displayed</span>
  </td>
);

const MarketingTable: FC<MarketingTable> = ({ columns, data, selectedEntity }) => {
  const setRowIdAsOrgId = ({ id }: { id: string }) => id;
  const { getTableProps, getTableBodyProps, rows, prepareRow, toggleAllRowsExpanded, toggleRowExpanded } = useTable(
    { columns, data, getRowId: setRowIdAsOrgId },
    useExpanded,
    useRowState
  );

  useEffect(() => {
    toggleAllRowsExpanded();

    const idsToBeCollapsed: string[] = [];
    getCollapsedOrgsIds(data, idsToBeCollapsed);
    idsToBeCollapsed.forEach((id) => toggleRowExpanded([id], false));
  }, [data, toggleAllRowsExpanded, toggleRowExpanded]);

  const isTablet = useMemo(() => window.matchMedia('(min-width: 769px) and (max-width: 1366px)').matches, []);

  return (
    <table style={{ maxHeight: 'calc(100vh - 400px)' }} className="w-full block overflow-y-auto" {...getTableProps()}>
      <tbody className="block" {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const isToManyDescendantsItem = row.original.id.includes(TO_MANY_DESCENDANTS);
          const isItemSelected = selectedEntity?.id === row.original.id;
          return (
            <tr
              data-testid={`table-row-exp-${!!row.isExpanded}`}
              {...row.getRowProps()}
              className={clsx(
                'flex items-center',
                isItemSelected ? 'bg-pale-blue' : !isToManyDescendantsItem && 'hover:bg-gray-10'
              )}
              style={{ paddingLeft: `${(isTablet ? 20 : 30) * row.depth + (isTablet ? 6 : 16)}px` }}
              key={`table-row-${row.id}`}
            >
              {isToManyDescendantsItem ? (
                <ToManyDescendants />
              ) : (
                row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps({
                        style: {
                          ...(cell.column.id === 'entity-name' && { width: 'calc(100% - 25px)', display: 'block' })
                        }
                      })}
                      key={`table-data-${cell.value}`}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MarketingTable;
